import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import { TripLeg } from '../../../data/models/reservation.model';
import { TripLegStatusBC } from '../../../data/models/trip-leg-status.model';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import SelectMenu from '../../forms/SelectMenu';

interface UnassignProviderDialogProps {
  tripLeg: TripLeg;
  open: boolean;
  close: () => void;
}

export default function UnassignProviderDialog({
  tripLeg,
  open,
  close,
}: UnassignProviderDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const rejectedReasonOptions = useAppSelector(
    lookupSlice.selectCallOutcomeRejectReasons,
  );

  const formHook = useForm({
    defaultValues: {
      unassignReason: 'REJECTED',
    },
  });

  const unassignReason = formHook.watch('unassignReason');

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSubmit = () => {
    dispatch(
      triplegSlice.rejectTripLegAsync({
        passengerUUID: tripLeg.passengerUUID,
        tripLegReferenceID: tripLeg.tripLegReferenceID!,
        tripLegRejectedReasonBC:
          (rejectedReasonOptions.find(
            (reason) =>
              reason.callOutcomeRejectReasonID === parseInt(unassignReason, 10),
          )?.callOutcomeRejectReasonBC as string) ?? undefined,
      }),
    );
  };

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('callOutcomeRejectReason'));
  }, []);

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>{t('triplegInventory.unassignTitle')}</DialogTitle>
      <DialogContent>
        <SelectMenu
          label={t('common.reason')}
          name="unassignReason"
          values={rejectedReasonOptions}
          valueAttr="callOutcomeRejectReasonID"
          labelAttr="callOutcomeRejectReasonName"
          control={formHook.control}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {t('triplegInventory.unassignNegativeText')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          autoFocus>
          {t('triplegInventory.unassignPositiveText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
